import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { AlertController, NavController, Platform } from '@ionic/angular/standalone';
import { Browser } from '@capacitor/browser';
import { IDEAApiService, IDEAMessageService, IDEATranslationsService } from '@idea-ionic/common';

import { environment as env } from 'src/environments/environment';
import { User } from '@models/user.model';

/**
 * The base URLs where the thumbnails are located.
 */
const THUMBNAILS_BASE_URL = env.idea.app.mediaUrl.concat('/images/', env.idea.api.stage, '/');
/**
 * A local fallback URL for the users avatars.
 */
const AVATAR_FALLBACK_URL = './assets/imgs/no-avatar.jpg';
/**
 * The local URL to the icon.
 */
const APP_ICON_PATH = './assets/icons/icon.svg';
/**
 * The local URL to the icon.
 */
const APP_ICON_WHITE_PATH = './assets/icons/star-white.svg';

@Injectable({ providedIn: 'root' })
export class AppService {
  private platform = inject(Platform);
  private navCtrl = inject(NavController);
  private alertCtrl = inject(AlertController);
  private message = inject(IDEAMessageService);
  private t = inject(IDEATranslationsService);
  private api = inject(IDEAApiService);

  initReady = false;
  authReady = false;

  private darkMode: boolean;

  user: User;

  linkToOpenViaFab: string;

  constructor() {
    this.darkMode = this.respondToColorSchemePreferenceChanges();
  }
  private respondToColorSchemePreferenceChanges(): boolean {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => (this.darkMode = e.matches));
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  /**
   * Whether we are running the app in developer mode (from localhost).
   */
  isDeveloperMode(): boolean {
    return env.debug;
  }
  /**
   * Open an alert to get the token for running requests against this project's API.
   */
  async getTokenId(): Promise<void> {
    const message = this.api.authToken as string;
    const alert = await this.alertCtrl.create({
      message,
      buttons: ['Thanks 🙌'],
      cssClass: 'selectable'
    });
    alert.present();
  }

  /**
   * Whether we should display a UX designed for smaller screens.
   */
  isInMobileMode(): boolean {
    return this.platform.width() < 768;
  }
  /**
   * Whether the current color scheme preference is set to dark.
   */
  isInDarkMode(): boolean {
    return this.darkMode;
  }

  /**
   * Reload the app.
   */
  reloadApp(): void {
    window.location.assign('');
  }
  /**
   * Navigate to a page by its path.
   */
  goTo(path: string[], options: { back?: boolean; root?: boolean; queryParams?: Params } = {}): void {
    if (options.back) this.navCtrl.navigateBack(path, options);
    else if (options.root) this.navCtrl.navigateRoot(path, options);
    else this.navCtrl.navigateForward(path, options);
  }
  /**
   * Navigate to a page in the tabs routing by its path.
   */
  goToInTabs(path: string[], options: { back?: boolean; root?: boolean; queryParams?: Params } = {}): void {
    this.goTo(['t', ...path], options);
  }
  /**
   * Close the current page and navigate back, optionally displaying an error message.
   */
  closePage(errorMessage?: string, pathBack?: string[]): void {
    if (errorMessage) this.message.error(errorMessage);
    try {
      this.navCtrl.back();
    } catch (_) {
      this.navCtrl.navigateBack(pathBack || []);
    }
  }

  /**
   * Get the URL to an image by its URI.
   */
  getImageURLByURI(imageURI: string): string {
    return THUMBNAILS_BASE_URL.concat(imageURI, '.png');
  }
  /**
   * Load a fallback URL when an avatar is missing.
   */
  fallbackAvatar(targetImg: any, star = false): void {
    const fallbackURL = star ? APP_ICON_WHITE_PATH : AVATAR_FALLBACK_URL;
    if (targetImg && targetImg.src !== fallbackURL) targetImg.src = AVATAR_FALLBACK_URL;
  }
  /**
   * Get the URL to the fallback avatar's image.
   */
  getAvatarFallbackURL(star = false): string {
    return star ? APP_ICON_WHITE_PATH : AVATAR_FALLBACK_URL;
  }

  /**
   * Open the URL in the browser.
   */
  async openURL(url: string): Promise<void> {
    if (this.platform.is('ios')) {
      // https://stackoverflow.com/a/39387533/13168139
      // Safari block the opening of any URL which is made inside an async call.
      // Usually ´Browser.open({ url, windowName: '_parent' })` would work, but there is a bug in Angular/Ionic that,
      // once you go back from the URL open to the app, blanks all the input fields
      this.linkToOpenViaFab = url;
      setTimeout((): void => {
        if (this.linkToOpenViaFab === url) this.linkToOpenViaFab = null;
      }, 10 * 1000);
    } else await Browser.open({ url });
  }

  /**
   * Utility to generate a numeric array.
   * Useful for skeleton interfaces.
   */
  generateNumericArray(length: number): number[] {
    return [...Array(length).keys()];
  }

  /**
   * Get the app's main icon.
   */
  getIcon(white = false): string {
    return white ? APP_ICON_WHITE_PATH : APP_ICON_PATH;
  }

  /**
   * Open a new window in the browser to download some data as a file.
   */
  downloadDataAsFile(data: any, type: string, fileName: string): void {
    const uri = `data:${type};charset=utf-8,${encodeURIComponent(data)}`;

    const downloadLink = document.createElement('a');
    downloadLink.href = uri;
    downloadLink.download = fileName;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  /**
   * Format a date in a short
   * format, e.g. "Mon, 1 Jan".
   */
  formatDateShort = (date: string | Date): string => {
    if (!(date instanceof Date)) date = new Date(date);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };
  formatTime(date: string | Date): string {
    if (!(date instanceof Date)) date = new Date(date);
    return new Date(date).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  async areYouSure(fn: (() => void) | (() => Promise<void>), destructive?: boolean): Promise<void> {
    const header = this.t._('COMMON.ARE_YOU_SURE');
    const message = destructive ? this.t._('COMMON.ACTION_IS_IRREVERSIBLE') : this.t._('COMMON.DO_YOU_WANT_TO_PROCEED');
    const buttons = [
      { text: this.t._('COMMON.CANCEL'), role: 'cancel' },
      {
        text: destructive ? this.t._('COMMON.DELETE') : this.t._('COMMON.CONFIRM'),
        role: destructive ? 'destructive' : 'confirm',
        handler: fn
      }
    ];
    const alert = await this.alertCtrl.create({ header, message, buttons });
    alert.present();
  }
}
